import { wgResource } from "./wgResource.js";
import formatCompilerError from "gl-format-compiler-error";

class wgShader extends wgResource {
    constructor(name, context) {
        super(name, context);
        this.vertexShader_ = null;
        this.fragmentShader_ = null;
        this.shaderProgram_ = null;
        this.uniformLocationMap_ = new Map(); // uniform location caching
        this.attribLocationMap_ = new Map(); // attrib location caching
    }

    bindProgram() {
        this.context_.gl().useProgram(this.shaderProgram_);
    }

    unbindProgram() {
        this.context_.gl().useProgram(null);
    }

    deleteProgram() {
        this.context_.gl().deleteShader(this.vertexShader_);
        this.context_.gl().deleteShader(this.fragmentShader_);
        this.context_.gl().deleteProgram(this.shaderProgram_);
        this.uniformLocationMap_.clear();
        this.attribLocationMap_.clear();
    }

    createShaderProgram(vertexShaderSrc, fragmentShaderSrc, attribLocations) {
        /* attribLocations: optional. Example {pos: 0, normal: 1} */
        const gl = this.context_.gl();
        this.vertexShader_ = this.compileShader(vertexShaderSrc, this.context_.gl().VERTEX_SHADER);
        this.fragmentShader_ = this.compileShader(fragmentShaderSrc, this.context_.gl().FRAGMENT_SHADER);

        if (this.vertexShader_ !== null && this.fragmentShader_ !== null) {
            this.shaderProgram_ = gl.createProgram();
            gl.attachShader(this.shaderProgram_, this.vertexShader_);
            gl.attachShader(this.shaderProgram_, this.fragmentShader_);

            // Set explicit attrib locations
            if ( attribLocations )
            {
                Object.keys( attribLocations ).forEach( (attribName) => {
                    gl.bindAttribLocation( this.shaderProgram_, attribLocations[attribName], attribName);
                });
            }

            gl.linkProgram(this.shaderProgram_);

            if (!gl.getProgramParameter(this.shaderProgram_, gl.LINK_STATUS)) {
                var errLog = gl.getProgramInfoLog(this.shaderProgram_);
                console.log(errLog);
                throw new Error(errLog);
            }
            let numAttribs = gl.getProgramParameter(this.shaderProgram_, gl.ACTIVE_ATTRIBUTES);
            for (let i = 0; i < numAttribs; i++) {
                const info = gl.getActiveAttrib(this.shaderProgram_, i);
                let aLoc = this.context_.gl().getAttribLocation(this.shaderProgram_, info.name);
                this.attribLocationMap_.set(info.name, aLoc);
            }
        }
    }

    bindAttribLocation(location, name)
    {
        const gl = this.context_.gl();
        gl.bindAttribLocation( this.shaderProgram_, location, name);
        gl.linkProgram(this.shaderProgram_);

        this.attribLocationMap_.clear();

        let numAttribs = gl.getProgramParameter(this.shaderProgram_, gl.ACTIVE_ATTRIBUTES);
        for (let i = 0; i < numAttribs; i++) {
            const info = gl.getActiveAttrib(this.shaderProgram_, i);
            let aLoc = this.context_.gl().getAttribLocation(this.shaderProgram_, info.name);
            this.attribLocationMap_.set(info.name, aLoc);
        }
    }

    compileShader(shaderSource, shaderType) {
        var shader = this.context_.gl().createShader(shaderType);
        this.context_.gl().shaderSource(shader, shaderSource);
        this.context_.gl().compileShader(shader);
        if (!this.context_.gl().getShaderParameter(shader, this.context_.gl().COMPILE_STATUS)) {
            var errLog = this.context_.gl().getShaderInfoLog(shader);
            var fmt = formatCompilerError(errLog, shaderSource, shaderType);
            console.warn(fmt.long);
            throw new Error(fmt.short);
        }
        return shader;
    }

    rebuildShader(vertexShaderSrc, fragmentShaderSrc) {
        this.deleteProgram();
        this.createShaderProgram(vertexShaderSrc, fragmentShaderSrc);
    }

    getAttribLocation(attributeName) {
        let attribLoc = this.attribLocationMap_.get(attributeName);

        if (attribLoc === undefined) {
            // not in the map
            console.log("invalid attrib name: " + attributeName);
            return null;
        }
        return attribLoc;
    }
    getShaderProgram() {
        return this.shaderProgram_;
    }

    getUniformLocation(uniformName) {
        let uniformLoc = this.uniformLocationMap_.get(uniformName);
        if (uniformLoc === undefined) {
            // not in the map
            let uLoc = this.context_.gl().getUniformLocation(this.shaderProgram_, uniformName);
            if (uLoc === null) {
                //  console.log("invalid uniform name: " + uniformName); // throw error?
                return null;
            }
            this.uniformLocationMap_.set(uniformName, uLoc);
            uniformLoc = uLoc;
        }
        return uniformLoc;
    }

    // Add new as we go along
    setTextureUniform(uniformName, textureUnit) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform1i(uLoc, textureUnit);
    }

    setTextureUniformV(uniformName, textureUnits) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform1iv(uLoc, textureUnits);
    }

    setUniform1i(uniformName, value) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform1i(uLoc, value);
    }

    setUniform1f(uniformName, value) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform1f(uLoc, value);
    }

    setUniform2f(uniformName, v0, v1) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform2f(uLoc, v0, v1);
    }

    setUniform3f(uniformName, v0, v1, v2) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform3f(uLoc, v0, v1, v2);
    }

    setUniform3fv(uniformName, vec) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform3fv(uLoc, vec);
    }

    setUniform4f(uniformName, v0, v1, v2, v3) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform4f(uLoc, v0, v1, v2, v3);
    }

    setUniform4iv(uniformName, v) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform4iv(uLoc, v);
    }

    setUniform4i(uniformName, i0, i1, i2, i3) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform4i(uLoc, i0, i1, i2, i3);
    }

    setUniform4fv(uniformName, vec) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniform4fv(uLoc, vec);
    }

    setUniformMat3fv(uniformName, mat) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniformMatrix3fv(uLoc, false, mat);
    }

    setUniformMat4fv(uniformName, mat) {
        const uLoc = this.getUniformLocation(uniformName);
        this.context_.gl().uniformMatrix4fv(uLoc, false, mat);
    }
}

export { wgShader };
